import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fileUrl, api } from '../../lib/api';
import { Download, Loader2, AlertCircle, Clapperboard, Play, Captions, Maximize2, X } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'image', label: 'Images' },
  { key: 'video', label: 'Videos' },
];

// What actually rendered the clip, read off the finished generation. Older
// clips predate the field and simply say nothing rather than guessing.
function clipCredit(g) {
  const parts = [];
  if (g.provider) parts.push(g.provider);
  if (g.model_used) parts.push(g.model_used);
  if (!parts.length) return undefined;
  const line = parts.join(' · ');
  return g.rescued_from
    ? `${line} (retried here after ${g.rescued_from} refused the prompt)`
    : line;
}

export default function ResultsStrip({ generations, onMakeVideo }) {
  const [tab, setTab] = useState('all');
  const [subsBusy, setSubsBusy] = useState(null);
  // Full-size viewer: the strip only ever showed a 112px thumbnail, so the one
  // way to inspect a render was the browser's own "open image in new tab".
  const [zoom, setZoom] = useState(null);
  // A failed render used to be a red icon and nothing else, so the reason it
  // failed, which the backend had already written down, was unreadable. The
  // messages carry the fix in them (lower the duration, top up that account),
  // and they are far too long for a 90px tile, so they open in full.
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    if (!zoom && !failure) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { setZoom(null); setFailure(null); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom, failure]);

  // Newest-first in the array, so the freshest 24 are the head. Beyond that a
  // long session was mounting hundreds of <video>/<img> at once; the rest live
  // in Photos & videos, linked at the end of the strip.
  const STRIP_CAP = 24;
  // avatar_video is a distinct kind from video (audio-driven, no fallback
  // provider) but renders the same way here -- a clip with a video_url.
  const isVideoKind = (g) => g.kind === 'video' || g.kind === 'avatar_video';
  const filtered = useMemo(() => {
    if (!generations) return [];
    if (tab === 'image') return generations.filter((g) => !isVideoKind(g));
    if (tab === 'video') return generations.filter(isVideoKind);
    return generations;
  }, [generations, tab]);
  const items = useMemo(() => filtered.slice(0, STRIP_CAP), [filtered]);
  const hiddenCount = filtered.length - items.length;

  if (!generations || generations.length === 0) return null;

  const makeSubs = async (g) => {
    setSubsBusy(g.id);
    try {
      const res = await api.generateSubtitles(g.id);
      const blob = new Blob([res.srt || ''], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `idolaris-${g.id.slice(0, 8)}.srt`; a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Subtitles ready', description: 'The .srt file has been downloaded.' });
    } catch (e) {
      toast({ title: 'Subtitles failed', description: e.message });
    }
    setSubsBusy(null);
  };

  return (
    <div className="border-t border-[#2A2A30] bg-[#0E0E10] px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex gap-1">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${tab === t.key ? 'bg-white text-black' : 'text-white/50 hover:text-white'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <span className="text-[11px] text-white/40">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1 no-scrollbar">
        {items.length === 0 && <p className="py-6 text-xs text-white/40">No {tab} results yet.</p>}
        {items.map((g) => {
          const isVideo = isVideoKind(g);
          const running = g.status === 'queued' || g.status === 'running';
          return (
            <div key={g.id} className={`group relative shrink-0 overflow-hidden rounded-lg border border-[#2A2A30] bg-[#141417] ${isVideo ? 'h-40 w-[90px]' : 'h-28 w-28'}`}>
              {running ? (
                <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-white/50">
                  <Loader2 size={18} className="animate-spin" />
                  <span className="text-[9px]">{isVideo ? 'rendering' : 'generating'}</span>
                </div>
              ) : g.status === 'failed' ? (
                <button onClick={() => setFailure(g)} data-testid="result-error"
                  title={g.error_message || 'This render failed. Click to see why.'}
                  className="flex h-full w-full flex-col items-center justify-center gap-1 px-1 text-center hover:bg-white/5">
                  <AlertCircle size={18} className="text-red-400" />
                  <span className="text-[9px] leading-tight text-white/50">
                    {g.error_message ? 'tap to read' : 'failed'}
                  </span>
                </button>
              ) : isVideo ? (
                g.video_url ? (
                  <>
                    {/* The model is named here and nowhere else in the flow,
                        because here it already ran and is a fact rather than a
                        promise the chain might not keep. */}
                    <video src={fileUrl(g.video_url)} title={clipCredit(g)} className="h-full w-full object-cover" muted loop playsInline
                      onMouseEnter={(e) => { const p = e.currentTarget.play(); if (p) p.catch(() => {}); }} onMouseLeave={(e) => e.currentTarget.pause()} />
                    <span className="absolute left-1.5 top-1.5 rounded bg-node-model/80 px-1.5 py-0.5 text-[9px] font-semibold text-white">VIDEO</span>
                    {/* Visible siempre, atenuado. Estaba en opacity-0 hasta el
                        hover, y en una tableta no hay hover: descargar el video
                        que se acaba de pagar era imposible desde el movil. */}
                    <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1.5 bg-gradient-to-t from-black/80 to-transparent p-1.5 opacity-80 transition-opacity group-hover:opacity-100">
                      <button onClick={() => setZoom(g)} title="View full size" data-testid="result-expand"
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30"><Maximize2 size={13} /></button>
                      <a href={fileUrl(g.video_url)} download target="_blank" rel="noreferrer" title="Download mp4" className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30"><Download size={13} /></a>
                      <button onClick={() => makeSubs(g)} disabled={subsBusy === g.id} title="Auto subtitles (.srt)" className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30 disabled:opacity-50">
                        {subsBusy === g.id ? <Loader2 size={13} className="animate-spin" /> : <Captions size={13} />}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <img src={fileUrl(g.url)} alt="" className="h-full w-full object-cover opacity-70" />
                    <span className="absolute left-1.5 top-1.5 rounded bg-amber-500/90 px-1.5 py-0.5 text-[9px] font-semibold text-black">MOCK</span>
                    <div className="absolute inset-x-0 bottom-0 bg-black/70 px-1 py-1 text-center text-[8px] leading-tight text-white/80">Add FAL_KEY for real motion</div>
                  </>
                )
              ) : g.url ? (
                <>
                  <img src={fileUrl(g.url)} alt="" onClick={() => setZoom(g)}
                    className="h-full w-full cursor-zoom-in object-cover" />
                  {/* Absent, not disabled, when video is switched off: a
                      greyed button still says the feature exists. */}
                  {onMakeVideo && (
                    <button onClick={() => onMakeVideo(g)} title="Make UGC video" data-testid="make-video-btn"
                      className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-node-model text-white shadow-md hover:brightness-110">
                      <Clapperboard size={13} />
                    </button>
                  )}
                  {/* Fondo propio y visibles siempre. Estaban en opacity-0 hasta
                      el hover, asi que en tactil no habia forma de descargar una
                      fotografia ya pagada -- el unico paso que de verdad importa
                      despues de hacerla. */}
                  <button onClick={() => setZoom(g)} title="View full size" data-testid="result-expand"
                    className="absolute bottom-1.5 left-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-black/55 text-white opacity-80 backdrop-blur-sm transition-opacity hover:bg-black/75 group-hover:opacity-100">
                    <Maximize2 size={13} />
                  </button>
                  <a href={fileUrl(g.url)} download target="_blank" rel="noreferrer" title="Download"
                    className="absolute bottom-1.5 right-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-black/55 text-white opacity-80 backdrop-blur-sm transition-opacity hover:bg-black/75 group-hover:opacity-100">
                    <Download size={13} />
                  </a>
                </>
              ) : null}
            </div>
          );
        })}
        {hiddenCount > 0 && (
          <Link to="/app/creations" data-testid="results-see-all"
            className="flex h-28 w-28 shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[#2A2A30] bg-[#141417] px-2 text-center text-[11px] text-white/60 transition-colors hover:border-white/30 hover:text-white">
            <span>+{hiddenCount} more</span>
            <span className="font-medium">See all in Photos &amp; videos →</span>
          </Link>
        )}
      </div>

      {failure && (
        <div data-testid="result-error-panel" onClick={() => setFailure(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-6">
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-xl border border-[#2A2A30] bg-[#141417] p-5">
            <div className="flex items-start gap-2.5">
              <AlertCircle size={18} className="mt-0.5 shrink-0 text-red-400" />
              <div className="min-w-0">
                <p className="font-medium text-white">This render failed</p>
                <p className="mt-0.5 text-[11px] text-white/40">
                  {isVideoKind(failure) ? 'Video' : 'Image'}
                  {failure.created_at ? ` · ${new Date(failure.created_at).toLocaleString()}` : ''}
                </p>
              </div>
            </div>
            <p className="mt-4 whitespace-pre-wrap break-words rounded-md border border-[#2A2A30] bg-[#0E0E10] p-3 text-xs leading-relaxed text-white/80">
              {failure.error_message || 'No reason was recorded for this one.'}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              {failure.error_message && (
                <button
                  onClick={() => {
                    navigator.clipboard?.writeText(failure.error_message);
                    toast({ title: 'Copied', description: 'The message is on your clipboard.' });
                  }}
                  className="rounded-md border border-[#2A2A30] px-3 py-1.5 text-xs text-white/70 hover:text-white">
                  Copy
                </button>
              )}
              <button onClick={() => setFailure(null)} data-testid="result-error-close"
                className="rounded-md bg-white px-4 py-1.5 text-xs font-medium text-black hover:bg-white/90">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {zoom && (
        <div data-testid="result-lightbox" onClick={() => setZoom(null)}
          className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-black/90 p-6">
          <div onClick={(e) => e.stopPropagation()} className="flex max-h-full flex-col items-center gap-3">
            {isVideoKind(zoom) && zoom.video_url ? (
              <video src={fileUrl(zoom.video_url)} controls autoPlay loop
                className="max-h-[80vh] max-w-[92vw] rounded-lg" />
            ) : (
              <img src={fileUrl(zoom.url)} alt="" className="max-h-[80vh] max-w-[92vw] rounded-lg object-contain" />
            )}
            <a href={fileUrl(zoom.video_url || zoom.url)} download target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md bg-white/15 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/25">
              <Download size={13} /> Download
            </a>
          </div>
          <button onClick={() => setZoom(null)} title="Close (Esc)" data-testid="result-lightbox-close"
            className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30">
            <X size={18} />
          </button>
        </div>
      )}
    </div>
  );
}
